import { useRef, useEffect, useState, memo } from 'react';
import type React from 'react';
import type { Midia } from '@/shared/types';

interface Props {
  midia: Midia;
  /**
   * Blob URL local gerado pelo mediaCache.toBlobUrl().
   * Se fornecido, substitui midia.url → garante playback 100% offline.
   * Se undefined, usa a URL original (fallback para rede / SW cache).
   */
  localSrc?: string;
  /** Ref externo para o elemento <video> — usado pelo Watchdog para monitorar saúde */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
}

/**
 * Renderiza um único slide de mídia (imagem ou vídeo).
 *
 * COMPATIBILIDADE CROSS-PLATFORM (TV Box / Tizen / WebOS):
 *
 *  Autoplay policy
 *    Browsers modernos bloqueiam autoplay sem interação do usuário.
 *    Sequência de fallback:
 *      1. Tenta play() normal (funciona se o browser permitir)
 *      2. Se bloqueado → força muted=true + volume=0 e tenta novamente
 *         (autoplay muted é universalmente permitido em todos os browsers)
 *      3. Define video.muted programaticamente além do atributo JSX para
 *         contornar o bug do React que nem sempre propaga o atributo muted.
 *
 *  Codec fallback (H.264/MP4)
 *    O elemento <source type="video/mp4"> declara explicitamente H.264,
 *    codec com aceleração hardware em praticamente todos os chips de TV Box.
 *    Browsers que não entenderem o type recaem para a URL original via <video src>.
 *
 *  Cache/Blob URL
 *    O Player resolve `localSrc` via mediaCache.toBlobUrl(midia.url).
 *    Se a Blob URL falhar (sandbox restrita em Tizen), toBlobUrl retorna
 *    a URL original automaticamente — MediaSlide não precisa tratar isso.
 */
function MediaSlide({ midia, localSrc, videoRef: externalVideoRef }: Props) {
  const internalVideoRef  = useRef<HTMLVideoElement>(null);
  const effectiveVideoRef = externalVideoRef ?? internalVideoRef;
  const [imgFailed, setImgFailed] = useState(false);

  // src efetivo: blob URL local se disponível, URL original como fallback
  const src = localSrc ?? midia.url;

  useEffect(() => {
    const video = effectiveVideoRef.current;
    if (!video) return;

    // Define muted programaticamente para contornar o bug do React:
    // o atributo `muted` em JSX nem sempre é propagado para o DOM no mount
    // em alguns browsers (Chromium < 70, Safari antigo).
    video.muted = true;
    video.volume = 0;
    video.currentTime = 0;

    // Tenta iniciar a reprodução. Se autoplay for bloqueado pela política
    // do browser (ex.: browser sem interação prévia), força muted e retry.
    const promise = video.play();
    if (promise !== undefined) {
      promise.catch(function() {
        // Autoplay bloqueado — força silent e tenta novamente.
        // Muted autoplay é universalmente permitido em todos os browsers,
        // incluindo políticas restritas de Smart TV.
        video.muted  = true;
        video.volume = 0;
        video.play().catch(function() {
          // Falhou mesmo com muted — browser extremamente restrito.
          // O Watchdog vai detectar o vídeo parado e avançar o slide.
        });
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-full h-full relative">

      {/* ── Mídia principal ──
           IMPORTANTE: nenhum ancestral do <video> pode ter opacity < 1 durante
           a transição. Quando opacity é animada num container que envolve o
           <video>, o browser força renderização software (sem hardware decode).
           Em fullscreen (1080p/4K) isso congela o vídeo. Por isso a animação
           de entrada é feita por um overlay preto que SOME, não pelo container. */}
      {midia.tipo === 'video' ? (
        <video
          ref={effectiveVideoRef}
          className="w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          loop
        >
          {/*
            <source> com type explícito instrui o browser a usar o decoder de
            hardware H.264, evitando fallback para decodificação por software
            (que trava em chips de TV Box com poucos cores de CPU).
            O atributo `src` no <video> serve como segunda garantia para
            browsers que ignoram <source> filhos (comportamento de alguns
            Chromium antigos embutidos em firmware de TV).
          */}
          <source src={src} type="video/mp4" />
        </video>
      ) : imgFailed ? (
        <div className="w-full h-full bg-slate-900 flex items-center justify-center">
          <svg className="w-20 h-20 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      ) : (
        <img
          src={src}
          alt={midia.titulo ?? 'Mídia'}
          className="w-full h-full object-cover"
          draggable={false}
          onError={() => setImgFailed(true)}
        />
      )}

      {/* ── Overlay de título e preço (gradiente no rodapé) ── */}
      {(midia.titulo || midia.preco !== null) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent px-8 py-6 sm:px-12 sm:py-8">
          <div className="animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
            {midia.titulo && (
              <h2
                className="text-white text-2xl sm:text-4xl font-bold leading-tight"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}
              >
                {midia.titulo}
              </h2>
            )}
            {midia.preco !== null && (
              <p
                className="text-orange-400 text-3xl sm:text-5xl font-black mt-1 tabular-nums"
                style={{ textShadow: '0 0 24px rgba(251,146,60,0.5), 0 2px 8px rgba(0,0,0,0.8)' }}
              >
                R$ {midia.preco.toFixed(2).replace('.', ',')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Overlay preto que desaparece ao montar — efeito fade-in sem tocar
           na opacity do <video>, preservando o hardware video overlay do GPU ── */}
      <div className="absolute inset-0 bg-black animate-fade-out pointer-events-none" />
    </div>
  );
}

export default memo(MediaSlide);
